import { useState, useEffect, useRef } from 'react';
import {
  Container, Box, Typography, Button, Paper, Grid,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Chip, Alert, CircularProgress, Tooltip,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Select, MenuItem, FormControl, InputLabel, Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { QRCodeSVG } from 'qrcode.react';
import { useReactToPrint } from 'react-to-print';
import { useAuth } from '../contexts/AuthContext';
import { sectorService } from '../services/sectorService';
import { db } from '../firebase/config';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { COLLECTIONS } from '../firebase/collections';

const BASE_URL = window.location.origin;

function QRPrintContent({ sector, qrUrl }) {
  return (
    <Box sx={{ p: 4, textAlign: 'center', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <Typography sx={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: '1.4rem', mb: 0.5 }}>
        EduWorks — Signaler un problème
      </Typography>
      <Typography sx={{ fontSize: '1rem', color: '#4a5568', mb: 2 }}>
        Secteur : <strong>{sector.name}</strong>
        {sector.building && ` — ${sector.building}`}
        {sector.floor && `, ${sector.floor}`}
      </Typography>
      <Box sx={{ display: 'inline-block', p: 2, border: '2px solid #3b5bdb', borderRadius: 2, mb: 2 }}>
        <QRCodeSVG value={qrUrl} size={200} level="H" />
      </Box>
      <Typography sx={{ fontSize: '0.85rem', color: '#94a3b8', mt: 1 }}>
        Scannez ce code avec votre téléphone pour signaler un problème
      </Typography>
      <Typography sx={{ fontSize: '0.75rem', color: '#cbd5e1', mt: 0.5 }}>
        {qrUrl}
      </Typography>
    </Box>
  );
}

const EMPTY_FORM = { name: '', building: '', floor: '', description: '', demandeurId: '' };

export default function SectorManagement() {
  const { userSchool, userRole } = useAuth();
  const [sectors, setSectors]         = useState([]);
  const [demandeurs, setDemandeurs]   = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [success, setSuccess]         = useState('');

  // Dialog création/édition
  const [dialogOpen, setDialogOpen]   = useState(false);
  const [editingSector, setEditingSector] = useState(null);
  const [form, setForm]               = useState(EMPTY_FORM);
  const [saving, setSaving]           = useState(false);

  // Dialog QR code
  const [qrSector, setQrSector]       = useState(null);
  const printRef                      = useRef();

  const schoolId = userSchool?.id;

  // Charger secteurs + demandeurs en parallèle
  useEffect(() => {
    if (!schoolId) return;
    loadData();
  }, [schoolId]);

  async function loadData() {
    setLoading(true);
    try {
      const [sectorsData, demaQuery] = await Promise.all([
        sectorService.getSectorsBySchool(schoolId),
        getDocs(query(
          collection(db, COLLECTIONS.USERS),
          where('schoolId', '==', schoolId),
          where('role', '==', 'requester')
        )),
      ]);
      setSectors(sectorsData);
      setDemandeurs(demaQuery.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('SectorManagement:', err);
      setError('Erreur lors du chargement des données.');
    } finally {
      setLoading(false);
    }
  }

  function openCreate() {
    setEditingSector(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  }

  function openEdit(sector) {
    setEditingSector(sector);
    setForm({
      name:        sector.name        || '',
      building:    sector.building    || '',
      floor:       sector.floor       || '',
      description: sector.description || '',
      demandeurId: sector.demandeurId || '',
    });
    setDialogOpen(true);
  }

  async function handleSave() {
    if (!form.name.trim() || !form.demandeurId) {
      setError('Le nom du secteur et le demandeur responsable sont obligatoires.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (editingSector) {
        await sectorService.updateSector(editingSector.id, {
          name:        form.name.trim(),
          building:    form.building.trim(),
          floor:       form.floor.trim(),
          description: form.description.trim(),
          demandeurId: form.demandeurId,
        });
        setSuccess('Secteur mis à jour.');
        setDialogOpen(false);
        loadData();
      } else {
        const { id, qrToken } = await sectorService.createSector({
          name:        form.name.trim(),
          building:    form.building.trim(),
          floor:       form.floor.trim(),
          description: form.description.trim(),
          demandeurId: form.demandeurId,
          schoolId,
        });
        setDialogOpen(false);
        // Ouvrir directement le QR code après création
        setQrSector({
          id,
          qrToken,
          name:        form.name.trim(),
          building:    form.building.trim(),
          floor:       form.floor.trim(),
          description: form.description.trim(),
          demandeurId: form.demandeurId,
          schoolId,
        });
        setSuccess('Secteur créé — QR code prêt à imprimer !');
        loadData();
      }
    } catch (err) {
      console.error('SectorManagement save:', err);
      setError('Erreur lors de la sauvegarde.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(sectorId) {
    if (!window.confirm('Désactiver ce secteur ? Les QR codes associés ne fonctionneront plus.')) return;
    try {
      await sectorService.deleteSector(sectorId);
      setSuccess('Secteur désactivé.');
      loadData();
    } catch {
      setError('Erreur lors de la suppression.');
    }
  }

  async function handleRegenerateToken(sector) {
    if (!window.confirm('Régénérer le QR code ? L\'ancien ne fonctionnera plus.')) return;
    try {
      const newToken = await sectorService.regenerateToken(sector.id);
      setSuccess('QR code régénéré.');
      setSectors(prev => prev.map(s => s.id === sector.id ? { ...s, qrToken: newToken } : s));
      if (qrSector?.id === sector.id) setQrSector(prev => ({ ...prev, qrToken: newToken }));
    } catch {
      setError('Erreur lors de la régénération.');
    }
  }

  const handlePrint = useReactToPrint({ contentRef: printRef });

  function getDemandeurName(demandeurId) {
    const d = demandeurs.find(u => u.id === demandeurId);
    return d ? (d.displayName || d.email) : '—';
  }

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
      <CircularProgress />
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <Typography variant="overline" sx={{ color: 'primary.main' }}>Administration</Typography>
          <Typography variant="h4" sx={{ fontFamily: "'Syne', sans-serif", fontWeight: 700, letterSpacing: '-0.02em' }}>
            Gestion des secteurs QR
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            Créez des secteurs, assignez un responsable et générez les QR codes à afficher.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ mt: 1 }}>
          Nouveau secteur
        </Button>
      </Box>

      {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Tableau des secteurs */}
      {sectors.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
          <QrCode2Icon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">Aucun secteur créé</Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
            Créez votre premier secteur pour générer un QR code.
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Créer un secteur
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Secteur</TableCell>
                <TableCell>Localisation</TableCell>
                <TableCell>Responsable</TableCell>
                <TableCell>QR Code</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sectors.map(sector => (
                <TableRow key={sector.id}>
                  <TableCell>
                    <Typography fontWeight={600}>{sector.name}</Typography>
                    {sector.description && (
                      <Typography variant="caption" color="text.secondary">{sector.description}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <LocationOnIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                      <Typography variant="body2">
                        {[sector.building, sector.floor].filter(Boolean).join(', ') || '—'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={getDemandeurName(sector.demandeurId)} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'inline-block' }}>
                      <QRCodeSVG
                        value={`${BASE_URL}/scan/${sector.qrToken}`}
                        size={48}
                        level="M"
                      />
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Voir / Imprimer le QR code">
                      <IconButton size="small" color="primary" onClick={() => setQrSector(sector)}>
                        <QrCode2Icon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Modifier">
                      <IconButton size="small" onClick={() => openEdit(sector)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Désactiver">
                      <IconButton size="small" color="error" onClick={() => handleDelete(sector.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Dialog création / édition ── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: "'Syne', sans-serif", fontWeight: 700 }}>
          {editingSector ? 'Modifier le secteur' : 'Nouveau secteur'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth required label="Nom du secteur" placeholder="Ex : Bâtiment C, Gymnase, Administration"
                value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Bâtiment" placeholder="Ex : Bâtiment A"
                value={form.building} onChange={e => setForm(p => ({ ...p, building: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Étage / Niveau" placeholder="Ex : RDC, 1er étage"
                value={form.floor} onChange={e => setForm(p => ({ ...p, floor: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} label="Description (optionnel)"
                placeholder="Ex : Zone couvrant les salles C01 à C20"
                value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Demandeur responsable *</InputLabel>
                <Select value={form.demandeurId} label="Demandeur responsable *"
                  onChange={e => setForm(p => ({ ...p, demandeurId: e.target.value }))}>
                  {demandeurs.length === 0 && (
                    <MenuItem disabled>Aucun compte demandeur trouvé</MenuItem>
                  )}
                  {demandeurs.map(d => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.displayName || d.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : (editingSector ? 'Enregistrer' : 'Créer')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog QR code ── */}
      <Dialog open={Boolean(qrSector)} onClose={() => setQrSector(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: "'Syne', sans-serif", fontWeight: 700 }}>
          QR Code — {qrSector?.name}
        </DialogTitle>
        <DialogContent>
          {qrSector && (
            <>
              {/* Zone d'impression */}
              <Box ref={printRef}>
                <QRPrintContent
                  sector={qrSector}
                  qrUrl={`${BASE_URL}/scan/${qrSector.qrToken}`}
                />
              </Box>
              <Divider sx={{ my: 2 }} />
              <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: 'text.disabled', wordBreak: 'break-all' }}>
                {BASE_URL}/scan/{qrSector.qrToken}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Tooltip title="Régénérer (invalide l'ancien QR code)">
            <Button startIcon={<RefreshIcon />} color="warning" size="small"
              onClick={() => handleRegenerateToken(qrSector)}>
              Régénérer
            </Button>
          </Tooltip>
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={() => setQrSector(null)}>Fermer</Button>
          <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint}>
            Imprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
